import Button from '@lyra/core/components/Button'
import ButtonGroup from '@lyra/core/components/ButtonGroup'
import Checkbox from '@lyra/core/components/Checkbox'
import { Plus } from '@lyra/core/components/Icon'
import NumberInput from '@lyra/core/components/Input/NumberInput'
import Section from '@lyra/core/components/Section'
import VideoText from '@lyra/core/components/VideoText'
import formatUSD from '@lyra/core/utils/formatUSD'
import { InstrumentType, Ticker } from '@lyra/web/constants/instruments'
import {
  OrderQuoteParams,
  OrderType,
  SetOrderQuoteParamsForInstrument,
  TimeInForce,
} from '@lyra/web/constants/order'
import { SetRfqParams } from '@lyra/web/constants/rfqs'
import useDebounce from '@lyra/web/hooks/useDebounce'
import useOrderQuote, { OrderQuoteHookParams } from '@lyra/web/hooks/useOrderQuote'
import useSubaccount from '@lyra/web/hooks/useSubaccount'
import { parseInstrumentName } from '@lyra/web/utils/instruments'
import {
  getDefaultOrderQuoteParams,
  getValidLimitPrice,
  getValidOrderAmount,
} from '@lyra/web/utils/order'
import { getRfqParamsFromOrderParams } from '@lyra/web/utils/rfqs'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { countDecimals, roundDownToStep } from '../../../utils/number'
import TradeFormAdvancedSettingsDropdown from './TradeFormAdvancedSettingsDropdown'
import TradeFormAmountInput from './TradeFormAmountInput'
import TradeFormAmountSlider from './TradeFormAmountSlider'
import TradeFormLimitPriceInput from './TradeFormLimitPriceInput'
import TradeFormReceipt from './TradeFormReceipt'
import TradeFormSpotTicket from './TradeFormSpotTicket'
import TradeFormSubmitOrderButton from './TradeFormSubmitOrderButton'
import TradeFormTicket from './TradeFormTicket'

type Props = {
  ticker: Ticker
  orderParams: OrderQuoteParams
  setOrderParams: SetOrderQuoteParamsForInstrument
  setRfqParams?: SetRfqParams
  onSubmitOrder?: () => void
  hideBuySellToggle?: boolean
  hideAdvancedSettings?: boolean
  hideReceipt?: boolean
  showMarkPrice?: boolean
  editOrderId?: string
}

const OrderbookTradeForm = ({
  ticker,
  orderParams: rawQuoteParams,
  setOrderParams: onChangeQuoteParams,
  setRfqParams,
  onSubmitOrder,
  hideBuySellToggle,
  hideAdvancedSettings,
  hideReceipt,
  showMarkPrice,
  editOrderId,
}: Props) => {
  const instrumentName = ticker.instrument_name

  // default to auto-limit price when not specified
  const limitPrice = getValidLimitPrice(+rawQuoteParams.limit_price, rawQuoteParams, ticker)

  // ensure trigger price input is rounded
  const triggerPrice = !!rawQuoteParams.trigger_type
    ? roundDownToStep(
        rawQuoteParams.trigger_price ? +rawQuoteParams.trigger_price : 0,
        +ticker.tick_size
      )
    : undefined

  const [expiryDays, setExpiryDays] = useState<number | null>(null)
  useEffect(() => {
    setExpiryDays(null)
    // when instrument, order type, trigger type changes
  }, [
    instrumentName,
    rawQuoteParams.order_type,
    rawQuoteParams.trigger_type,
    rawQuoteParams.time_in_force,
  ])

  const quoteParams: OrderQuoteHookParams = useMemo(
    () => ({
      ...rawQuoteParams,
      // default to min amount
      amount: getValidOrderAmount(+rawQuoteParams.amount, rawQuoteParams, ticker).toString(),
      // default to best offer if limit price not specified
      limit_price: limitPrice.toString(),
      // only include trigger price params when trigger_type is set
      trigger_price: triggerPrice?.toString(),
      // "mark" is always set for tigger_price_type
      trigger_price_type: rawQuoteParams.trigger_type ? 'mark' : undefined,
      // inject expiry days
      expiryDays,
    }),
    [rawQuoteParams, ticker, limitPrice, triggerPrice, expiryDays]
  )

  const handleSubmitOrder = useCallback(() => {
    onChangeQuoteParams((params) => ({
      ...params,
      amount: '0',
    }))

    if (onSubmitOrder) {
      onSubmitOrder()
    }
  }, [onSubmitOrder, onChangeQuoteParams])

  const timeInForce = quoteParams.time_in_force
  const handleSetTimeInForce = useCallback(
    (timeInForce: TimeInForce) =>
      onChangeQuoteParams((params) => ({ ...params, time_in_force: timeInForce })),
    [onChangeQuoteParams]
  )

  const { subaccount } = useSubaccount()
  const openPosition = useMemo(
    () =>
      subaccount?.positions
        .filter((p) => !!+p.amount)
        .find((position) => position.instrument_name === instrumentName),
    [subaccount, instrumentName]
  )

  const tickSize = +ticker.tick_size
  const priceDps = countDecimals(tickSize)

  const handleFlipDirection = useCallback(
    (isBuy: boolean) => {
      if (isBuy === (quoteParams.direction === 'buy')) {
        // already set in that direction
        return
      }
      onChangeQuoteParams((params) => ({
        ...getDefaultOrderQuoteParams(params.instrument_name),
        order_type: params.order_type,
        trigger_type: params.trigger_type,
        direction: isBuy ? 'buy' : 'sell',
        amount: params.amount,
      }))
    },
    [quoteParams, onChangeQuoteParams]
  )

  const isBuy = rawQuoteParams.direction === 'buy'

  const handleSetTriggerPrice = useCallback(
    (triggerPrice: number) =>
      onChangeQuoteParams((params) => ({
        ...params,
        trigger_price: roundDownToStep(triggerPrice, tickSize).toString(),
      })),
    [onChangeQuoteParams, tickSize]
  )

  const handleSetReduceOnly = useCallback(
    (reduceOnly: boolean) =>
      onChangeQuoteParams((params) => ({ ...params, reduce_only: reduceOnly })),
    [onChangeQuoteParams]
  )

  const { data: quote, isLoading, error } = useOrderQuote(useDebounce(quoteParams, 100))

  const [isExpiryOpen, setIsExpiryOpen] = useState(false)

  const instrument = parseInstrumentName(instrumentName)

  const isReduceOnly = !!quoteParams.reduce_only

  const isTrigger = !!quoteParams.trigger_type

  const isPerp = instrument?.type === InstrumentType.Perps
  const isOption = instrument?.type === InstrumentType.Options
  const isSpot = instrument?.type === InstrumentType.Spot

  const showLeverage = (isPerp || isSpot) && !isTrigger && !editOrderId

  const canReduceOnly = [TimeInForce.FillOrKill, TimeInForce.IoC].includes(timeInForce) || isTrigger

  const showRfqSwitch = !isTrigger && isOption

  const handleChangeLimitPrice = useCallback(
    (limitPrice: number) =>
      onChangeQuoteParams((params) => ({
        ...params,
        limit_price: getValidLimitPrice(limitPrice, quoteParams, ticker).toString(),
      })),
    [onChangeQuoteParams, quoteParams, ticker]
  )

  return (
    <>
      {!hideBuySellToggle && !editOrderId ? (
        <Section.XStack>
          <ButtonGroup flexGrow={1} flexShrink={1} isEqualWidth>
            {/* TODO: @earthtojake remove outlined prop */}
            <Button
              label="Sell"
              color="red"
              isOutlined={!isBuy}
              onPress={() => handleFlipDirection(false)}
            />
            <Button
              label="Buy"
              color="green"
              isOutlined={isBuy}
              onPress={() => handleFlipDirection(true)}
            />
          </ButtonGroup>
        </Section.XStack>
      ) : null}
      {quoteParams.trigger_type && triggerPrice !== undefined ? (
        <Section.XStack>
          <NumberInput
            width="100%"
            value={triggerPrice}
            formatValue={(val) => formatUSD(val, { dps: priceDps })}
            onChangeValue={handleSetTriggerPrice}
            leftContent={<VideoText color="secondary">Trigger Price</VideoText>}
            textAlign="right"
          />
        </Section.XStack>
      ) : null}
      {quoteParams.order_type === OrderType.Limit ? (
        <TradeFormLimitPriceInput
          ticker={ticker}
          quoteParams={rawQuoteParams}
          onChangeQuoteParams={onChangeQuoteParams}
        />
      ) : null}
      <TradeFormAmountInput
        ticker={ticker}
        rawQuoteParams={rawQuoteParams}
        onChangeQuoteParams={onChangeQuoteParams}
      />
      {showLeverage ? (
        <TradeFormAmountSlider
          quote={quote}
          ticker={ticker}
          quoteParams={quoteParams}
          onChangeQuoteParams={onChangeQuoteParams}
        />
      ) : null}
      {!hideAdvancedSettings && (showRfqSwitch || canReduceOnly) ? (
        <Section.XStack justifyContent="space-between" alignItems="center">
          {canReduceOnly ? (
            <Checkbox
              label="Reduce Only"
              isChecked={isReduceOnly}
              onPress={() => handleSetReduceOnly(!isReduceOnly)}
            />
          ) : null}
          {showRfqSwitch && setRfqParams ? (
            <Button
              size="sm"
              label="Add Option"
              leftIcon={<Plus />}
              onPress={() => setRfqParams(getRfqParamsFromOrderParams(quoteParams))}
            />
          ) : null}
        </Section.XStack>
      ) : null}
      {!hideAdvancedSettings ? (
        <TradeFormAdvancedSettingsDropdown
          isOpen={isExpiryOpen}
          expiryDays={expiryDays}
          params={quoteParams}
          onChangeOpen={setIsExpiryOpen}
          onChangeTimeInForce={handleSetTimeInForce}
          onChangeExpiryDays={setExpiryDays}
        />
      ) : null}
      {isSpot ? (
        <TradeFormSpotTicket
          ticker={ticker}
          quoteParams={quoteParams}
          quote={quote}
          isLoading={isLoading}
        />
      ) : (
        <TradeFormTicket
          ticker={ticker}
          quoteParams={quoteParams}
          quote={quote}
          isLoading={isLoading}
          showMarkPrice={showMarkPrice}
        />
      )}
      <TradeFormSubmitOrderButton
        // IMPORTANT!! pass in modified quote params
        quoteParams={quoteParams}
        quote={quote}
        error={error}
        isLoading={isLoading}
        onSubmitOrder={handleSubmitOrder}
        editOrderId={editOrderId}
      />
      {!hideReceipt && !editOrderId ? (
        <>
          <Section.Separator />
          <TradeFormReceipt
            onPressLimitPrice={handleChangeLimitPrice}
            ticker={ticker}
            params={quoteParams}
            quote={quote}
            isLoading={isLoading}
          />
        </>
      ) : null}
    </>
  )
}

export default OrderbookTradeForm
